/**
 * Audit Logger — Fire-and-Forget
 *
 * Lightweight, non-blocking audit logger for universal data-access auditing (F08).
 * Inserts into the `audit_log` table without awaiting — never throws, never blocks.
 *
 * Auto-flags `access_pii` when entityType is 'employees', 'suspects', or 'users'.
 * Auto-captures `navigator.userAgent` when running in a browser.
 * Auto-resolves userId from Supabase session when not provided.
 *
 * Usage:
 * ```typescript
 * import { auditLogger } from '@/services/auditLogger';
 *
 * // With explicit userId (e.g. from AuthContext)
 * auditLogger.log({ tenantId, userId, action: 'view', entityType: 'alerts' });
 *
 * // Without userId — auto-resolved from session (used in repositories)
 * auditLogger.log({ tenantId, action: 'view', entityType: 'alerts' });
 * ```
 *
 * @module services/auditLogger
 */

import { supabase } from './supabase';
import { PII_ENTITY_TYPES } from './schemas/auditSchemas';
import type { AuditAction } from './schemas/auditSchemas';

interface AuditInput {
  tenantId: string;
  userId?: string;
  action: AuditAction;
  entityType: string;
  entityId?: string;
  details?: Record<string, unknown>;
}

// Cached session user ID to avoid repeated async calls
let _cachedUserId: string | null = null;
let _cacheExpiry = 0;

/**
 * Get current user ID from Supabase session (cached for 60s).
 * Returns null if no session or error — never throws.
 */
function getCachedUserId(): string | null {
  if (_cachedUserId && Date.now() < _cacheExpiry) {
    return _cachedUserId;
  }
  // Refresh async (fire-and-forget)
  supabase.auth.getSession().then(({ data }) => {
    _cachedUserId = data.session?.user?.id || null;
    _cacheExpiry = Date.now() + 60_000; // 60s cache
  }).catch(() => {
    // Silently fail
  });
  return _cachedUserId;
}

/**
 * Get userAgent string, safe for SSR/worker contexts.
 */
function getUserAgent(): string | null {
  if (typeof navigator !== 'undefined') {
    return navigator.userAgent;
  }
  return null;
}

/**
 * Singleton audit logger.
 * All methods are fire-and-forget — they NEVER throw and NEVER block the caller.
 */
export const auditLogger = {
  /**
   * Log an audit entry. Fire-and-forget.
   *
   * - userId is optional — auto-resolved from Supabase session if not provided
   * - Auto-detects PII access based on entityType
   * - Auto-captures userAgent
   * - Catches and console.error's any failures — never propagates
   */
  log(input: AuditInput): void {
    // Guard: skip if missing tenant
    if (!input.tenantId) {
      return;
    }

    // Resolve userId: explicit > cached session > skip
    const userId = input.userId || getCachedUserId();
    if (!userId) {
      return;
    }

    // Auto-flag PII access
    const isPII = (PII_ENTITY_TYPES as readonly string[]).includes(input.entityType);
    const action = isPII ? 'access_pii' : input.action;

    // Fire-and-forget insert (no await)
    supabase
      .from('audit_logs')
      .insert({
        tenant_id: input.tenantId,
        user_id: userId,
        action,
        resource_type: input.entityType,
        resource_id: input.entityId || null,
        metadata: {
          ...input.details,
          ...(isPII ? { original_action: input.action } : {}),
        },
        request_meta: {
          user_agent: getUserAgent(),
          ip_address: null,
        },
      })
      .then(({ error }: { error: unknown }) => {
        if (error) {
          console.error('[auditLogger] Failed to log:', error);
        }
      })
      .catch((err: unknown) => {
        console.error('[auditLogger] Insert exception:', err);
      });
  },
};

export default auditLogger;
